import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { CoreApplicationContext } from '@eventbrite/context-gen';
import {
    EBEnvsEnum,
    useRealUserMonitoring,
} from '@eventbrite/real-user-monitoring';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { EnvProvider, LocationProvider } from './context';
import { Routes } from './routes';
import { PointOfInterestAppProps, ThingsToDoAppProps } from './types';

const isProd = (domain: string) => domain?.includes('eventbrite');

export const App: React.FC<
    (ThingsToDoAppProps | PointOfInterestAppProps) & CoreApplicationContext
> = (props) => {
    const EB_DOMAIN = props.env.ebDomain;
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                retry: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
                refetchOnMount: false,
                staleTime: Infinity,
            },
        },
    });

    const DATADOG_SAMPLE_RATE = isProd(EB_DOMAIN) ? 100 : 0;
    useRealUserMonitoring({
        sessionSampleRate: DATADOG_SAMPLE_RATE,
        version: props.app_version,
        service: props.app_name,
        enabledEnvironments: [EBEnvsEnum.PROD],
    });

    return (
        <QueryClientProvider client={queryClient}>
            <EnvProvider
                env={props.env}
                request={props.request}
                user={props.user}
                app_version={props.app_version}
                app_name={props.app_name}
                isBotRequest={props.isBotRequest}
            >
                <LocationProvider
                    shortPlaceName={props.shortPlaceName}
                    placeName={props.placeName}
                    location_slug={props.location_slug}
                >
                    <Routes {...props} />
                </LocationProvider>
                {isProd(EB_DOMAIN) ? null : (
                    <ReactQueryDevtools initialIsOpen={false} />
                )}
            </EnvProvider>
        </QueryClientProvider>
    );
};
